import { WheelEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { AlignLeftOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { Button, Input, Row, Col, message, Select } from 'antd';
import { Raffle, Ticket } from '../Raffles/interfaces';
import moment from 'moment';
import TicketsModal from './TicketsModal';
import TicketsModalBuy from './TicketsModalBuy';
import { Table, MenuItem, TableContainer, Paper, TableHead, TableRow, TableCell, TableBody, CircularProgress } from "@mui/material";
import { useParams } from "react-router-dom";
import { limit, orderBy, startAfter, Timestamp, where } from "firebase/firestore";
import { getGenericDocById, update, updateByFilters } from "../../services/firestore";
import useCollection, { PropsUseCollection } from "../../hooks/useCollection";
import dayjs from "dayjs";
import { QueryConstraint } from "firebase/firestore/lite";

interface BuildQueryProps {
  _status?: string;
  _search?: string;
}

const Tickets = () => {
  const { raffleId } = useParams<{ raffleId: string; }>();
  const [releasing, setReleasing] = useState(false);
  const [openBuyModal, setOpenBuyModal] = useState(false);
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [openInfo, setOpenInfo] = useState<boolean>(false);
  const [raffle, setRaffle] = useState<Raffle | null>(null);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [status, setStatus] = useState("");
  const [fieldSearch, setFieldSearch] = useState("phone");
  const [search, setSearch] = useState("");

  const queryBase = useMemo<QueryConstraint[]>(() => {
    return [
      where("raffleId", "==", raffleId),
      orderBy("number", "asc"),
      limit(100)
    ];
  }, [raffleId]);

  const [propsUseCollection, setPropsUseCollection] = useState<PropsUseCollection>({
    path: "tickets",
    queryConstraint: queryBase,
  });

  const buildQuery = useCallback(({ _status, _search }: BuildQueryProps) => {
    const query = [...queryBase];

    if (_status) {
      query.unshift(where("status", "==", _status));
    }

    if (_search) {
      query.unshift(where(fieldSearch, "==", _search));
    }

    return query as QueryConstraint[];
  }, [queryBase, fieldSearch]);

  const { data, loading, count } = useCollection<Ticket>(propsUseCollection);

  useEffect(() => {
    if (loading) return;

    setTickets(prev => [...prev, ...data]);
  }, [loading, data]);

  useEffect(() => {
    if (!raffleId) return;

    const getRaffle = async () => {
      const _raffle = await getGenericDocById<Raffle>("raffles", raffleId);

      setRaffle(_raffle);
    };

    getRaffle();
  }, [raffleId]);

  useEffect(() => {
    if (!raffle || loading) return;

    const now = moment();

    setTickets(prev => prev.map((ticket) => {
      //si no esta pagado y tiene mas de 1 dia esta vencido
      const diff = ticket.reservationDate && ticket.status !== "Pagado" ? now.diff(moment(ticket.reservationDate.toDate()), "days") : null;

      return diff
        ? {
          ...ticket,
          status: "Vencido",
          raffle
        } : { ...ticket, raffle };
    }));

  }, [raffle, loading, setTickets]);

  const resetTable = ({ _status, _search }: BuildQueryProps) => {
    setTickets([]);
    setPropsUseCollection({
      path: "tickets",
      queryConstraint: buildQuery({ _status, _search }),
      withCount: Boolean(_status || _search)
    });
  };

  const onWheel = (e: WheelEvent<HTMLDivElement>) => {
    if (!raffle || loading || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;

    let bottom = false;
    const ua = navigator.userAgent.toLowerCase();
    const target = e.currentTarget;

    if (ua.indexOf('safari') !== -1) {
      if (ua.indexOf('chrome') > -1) {
        bottom = parseInt((target.scrollHeight - target.scrollTop).toString()) === target.clientHeight;
      } else {
        bottom = (target.scrollHeight - target.scrollTop) < (target.clientHeight + 2);
      }
    }

    const last = tickets[tickets.length - 1].number;

    if (bottom && tickets.length < (typeof count === "number" ? count : raffle.countTickets)) {
      setPropsUseCollection((prev) => {
        return {
          ...prev,
          queryConstraint: [
            ...buildQuery({ _status: status, _search: search }),
            startAfter(last),
          ],
        };
      });
    }
  };

  const releaseTickets = async () => {
    if (releasing) return;

    try {
      setReleasing(true);

      await updateByFilters(
        "tickets",
        [
          where("raffleId", "==", raffleId),
          where("status", "==", "Reservado"),
          where("reservationDate", "<", Timestamp.fromDate(dayjs().subtract(1, "days").toDate())),
        ],
        {
          status: "Libre",
          reservationDate: null,
          buyer: "",
          phone: null,
          state: ""
        }
      );

      setPropsUseCollection(prev => ({
        ...prev,
        path: "",
      }));
      setSearch("");
      setStatus("");

      setTimeout(() => {
        resetTable({});
      }, 1);

      message.success("Boletos liberados con exito");
    } catch (error) {
      console.log(error);
    } finally {
      setReleasing(false);
    }
  };

  return (
    <div style={{ marginTop: 20, padding: 20 }}>
      <Row
        align="middle"
        justify="space-between"
      >
        <Col>
          <Button
            loading={releasing}
            type="primary"
            onClick={releaseTickets}
          >
            Liberar boletos vencidos
          </Button>
        </Col>
        <Col>
          <b>Total de boletos: {typeof count === "number" ? count : raffle?.countTickets}</b>
        </Col>
      </Row>
      <br />
      <br />
      <Row
        gutter={10}
      >
        <Col>
          <Select
            placeholder="Filtrar estado de boleto"
            style={{ width: 200, marginRight: 10 }}
            value={status}
            onChange={value => {
              setStatus(value);
              resetTable({ _status: value, _search: search });
            }}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="Libre">Libres</MenuItem>
            <MenuItem value="Reservado">Reservados</MenuItem>
            {/*      <MenuItem value="Vencido">Vencidos</MenuItem> */}
            <MenuItem value="Pagado">Pagados</MenuItem>
          </Select>
        </Col>
        <Col>
          <Select
            placeholder="Filtrar por"
            style={{ width: 120 }}
            onChange={value => setFieldSearch(value)}
            value={fieldSearch}
          >
            <MenuItem value="phone">Teléfono</MenuItem>
            <MenuItem value="buyer">Nombre</MenuItem>
          </Select>
        </Col>
        <Col>
          <Input
            value={search}
            onChange={e => {
              setSearch(e.target.value);
            }}
            placeholder="Buscar..."
          />
        </Col>
        <Col>
          <Button
            onClick={() => resetTable({ _status: status, _search: search })}
          >
            Buscar
          </Button>
        </Col>
      </Row>

      <br />
      <br />
      <TableContainer component={Paper} style={{ overflowY: "auto", height: "62vh" }} onWheel={onWheel}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell component="th"><b>Número</b></TableCell>
              <TableCell component="th"><b>Comprador</b></TableCell>
              <TableCell component="th"><b>Teléfono</b></TableCell>
              <TableCell component="th"><b>Estado</b></TableCell>
              <TableCell component="th"><b>Reserva</b></TableCell>
              <TableCell component="th"><b>Estado</b></TableCell>
              <TableCell component="th"><b>Recibo</b></TableCell>
              <TableCell component="th"><b>Comprar</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tickets.map((row: Ticket) => (
              <TableRow key={row?.id}>
                <TableCell scope="row">
                  {row.number.toString().padStart(6, "0")}
                </TableCell>
                <TableCell scope="row">
                  {row.buyer}
                </TableCell>
                <TableCell scope="row">
                  {row.phone}
                </TableCell>
                <TableCell scope="row">
                  {row.state}
                </TableCell>
                <TableCell scope="row">
                  {row.reservationDate ? moment(row.reservationDate.toDate()).format("DD/MM/YYYY hh:mm a") : "Sin reserva"}
                </TableCell>
                <TableCell scope="row">
                  {
                    <Select
                      style={{ width: 200 }}
                      value={row.status}
                      onChange={async (value) => {
                        try {
                          await update(
                            "tickets",
                            row.id,
                            value === "Libre"
                              ? {
                                status: value,
                                reservationDate: null,
                                buyer: "",
                                phone: null,
                                state: ""
                              }
                              : {
                                status: value
                              }
                          );

                          setTickets(prev =>
                            prev.map(t => (
                              t.id === row.id
                                ? value === "Libre"
                                  ? { ...t, status: value, reservationDate: null, buyer: "", phone: null, state: "" }
                                  : { ...t, status: value }
                                : t
                            ))
                          );
                        } catch (error) {
                          console.log(error);
                          message.error("Error al actualizar el estado del boleto");
                        }
                      }}
                    >
                      <MenuItem value="Libre">Libre</MenuItem>
                      <MenuItem value="Reservado" disabled>Reservado</MenuItem>
                      <MenuItem value="Vencido" disabled style={{ color: "red" }}>Vencido</MenuItem>
                      <MenuItem value="Pagado" disabled={row.status === "Libre"}>Pagado</MenuItem>
                    </Select>
                  }
                </TableCell>
                <TableCell>
                  {row.status === "Pagado" && <AlignLeftOutlined onClick={() => { setTicket(row); setOpenInfo(true); }} />}
                </TableCell>
                <TableCell>
                  {
                    row.status !== "Pagado" && <ShoppingCartOutlined onClick={() => { setTicket(row); setOpenBuyModal(true); }} />
                  }
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {
        loading && <div
          style={{
            paddingTop: 10,
            height: 80,
            maxHeight: 80,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <div style={{ fontSize: 20 }}>Cargando boletos...</div>
          <div>
            <CircularProgress size={50} style={{ color: "orangered", padding: 10 }} />
          </div>
        </div>
      }
      <TicketsModal
        open={openInfo}
        onClose={() => setOpenInfo(false)}
        ticket={ticket}
      />
      <TicketsModalBuy
        open={openBuyModal}
        onClose={(saved: boolean, dataBuyer: any) => {

          if (saved) {
            setTickets(t =>
              t.map(t => (
                t?.id === ticket?.id
                  ? {
                    ...t, status: "Pagado",
                    reservationDate: dataBuyer.reservationDate,
                    buyer: dataBuyer.buyer,
                    phone: dataBuyer.phone,
                    state: dataBuyer.state
                  }
                  : t
              )) as Ticket[]
            );
          }
          setOpenBuyModal(false);
          setTicket(null);
        }}
        idTicket={ticket?.id}
      />
    </div>
  );
};

export default Tickets;
